import React from 'react';
import {
  Content,
  Row,
  Col,
  Title,
  Pagination,
  Button,
  Text,
} from 'components';
import { RentalListTable, SideMenu } from '../components';
import OrderListTable from '../components/OrderListTable/OrderListTable';
import { DatePicker, Empty, Spin } from 'antd';
import MessageAlert from 'utils';
import dayjs from 'dayjs';
import { encrypt } from 'utils/Crypto';

const TAP = [
  {
    label: '택배배송',
    value: 'DELIVERY',
  },
  {
    label: '정기배송',
    value: 'SUBSCRIPTION',
  },
  {
    label: '매장픽업',
    value: 'STORE_PICKUP',
  },
  {
    label: '렌탈',
    value: 'RENTAL',
  },
];

const PERIOD = [
  {
    label: '1개월',
    value: 1,
    key: 'month',
  },
  {
    label: '3개월',
    value: 3,
    key: 'month',
  },
  {
    label: '6개월',
    value: 6,
    key: 'month',
  },
  {
    label: '1년',
    value: 1,
    key: 'year',
  },
];

const DATE_FORMAT = 'YYYY/MM/DD';
const COUNT = 5;

const VIRTUAL_ACCOUNT_BANK = {
  '03': '기업은행',
  '04': '국민은행',
  '05': '하나은행',
  11: '농협중앙',
  20: '우리은행',
  23: 'SC제일은행',
  26: '신한은행',
  31: '대구은행',
  32: '부산은행',
  34: '광주은행',
  37: '전북은행',
  39: '경남은행',
  71: '우체국',
};

const getVirtualAccountDepositStatus = (order = {}) => {
  const depositStatuses = (order?.parcel_service ?? [])
    .map((parcel) => parcel?.deposit_status)
    .filter(Boolean);

  if (depositStatuses.length) {
    if (depositStatuses.every((status) => status === 'COMPLETED')) {
      return '입금완료';
    }
    if (depositStatuses.some((status) => status === 'COMPLETED')) {
      return '일부 입금완료';
    }
    return '입금대기';
  }

  if (order?.payment_state === 'PENDING') return '입금대기';
  if (order?.payment_state === 'COMPLETED') return '입금완료';
  return '입금상태 확인 필요';
};

const VirtualAccountInfo = ({ order, screenSize }) => {
  if (order?.payment_method !== 'VIRTUAL_ACCOUNT') return null;

  const paymentInfo = order?.payment_info ?? {};
  const bankName =
    paymentInfo.bankName ??
    paymentInfo.bank_name ??
    VIRTUAL_ACCOUNT_BANK[paymentInfo.authno] ??
    paymentInfo.authno;
  const accountNumber =
    paymentInfo.virtualAccountNumb ??
    paymentInfo.virtual_account_numb ??
    paymentInfo.accountNumb ??
    paymentInfo.account_number ??
    paymentInfo.isscd;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: screenSize >= 500 ? 'row' : 'column',
        gap: screenSize >= 500 ? 32 : 6,
        padding: '14px 16px',
        marginBottom: 20,
        backgroundColor: '#f7f7f7',
      }}
    >
      <Text size={14}>
        입금은행: <strong>{bankName ?? '-'}</strong>
      </Text>
      <Text size={14}>
        계좌번호: <strong>{accountNumber ?? '-'}</strong>
      </Text>
      <Text size={14}>
        입금상태: <strong>{getVirtualAccountDepositStatus(order)}</strong>
      </Text>
    </div>
  );
};

const MypageOrderPresenter = (props) => {
  /* ===== Props ===== */
  const {
    order,
    isLoading,
    setIsLoading,
    onOrderStatus,
    navigate,
    selectedTab,
    screenSize,
    // type,
  } = props;

  /* ===== Variables ===== */
  const styles = {
    titleLayout: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      paddingBottom: 32,
      borderBottom: '2px solid var(--black-color)',
    },
    tabContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 32,
      borderBottom: '2px solid var(--black-color)',
    },
    tab: {
      width: screenSize >= 768 ? 160 : 130,
      height: screenSize >= 768 ? 66 : 55,
      lineHeight: screenSize >= 768 ? '66px' : '55px',
      textAlign: 'center',
      cursor: 'pointer',
      fontSize: screenSize >= 768 ? 16 : 14,
      border: '1px solid gray',
      backgroundColor: 'transparent',
      color: 'gray',
    },
    activeTab: {
      fontWeight: 'bold',
      backgroundColor: 'black',
      color: 'white',
      border: '2px solid black',
    },
    emptyState: {
      textAlign: 'center',
      padding: 20,
      fontSize: 16,
      color: 'gray',
    },
    paginationButton: {
      color: 'var(--black-color)',
      width: 28,
      height: 28,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    button: {
      width: '25%',
      borderRadius: '0',
      border: '1px solid var(--light-gray-color)',
      backgroundColor: '#fff',
      color: 'var(--light-gray-color)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: screenSize >= 768 ? 16 : 14,
      cursor: 'pointer',
    },
    activeButton: {
      border: '1px solid var(--black-color)',
      color: 'var(--black-color)',
    },
  };

  // 주문 내역
  const [orderList, setOrderList] = React.useState([]);

  // Period Date
  const [selected, setSelected] = React.useState(0);
  // Period Range
  const [period, setPeriod] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1); // 현재 페이지

  /* ===== Function ===== */
  /**
   * 페이징
   * --
   * @param {number} page
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
   * 검색기간 설정
   * --
   */
  const handleDateChange = (date, key) => {
    if (selected === date) {
      setSelected(0);
      setPeriod([]);
    } else {
      const start = dayjs().subtract(date, key);

      setSelected(`${date}-${key}`);
      setPeriod([start, dayjs().endOf('day')]);
    }
  };

  /**
   * 기간 검색
   * --
   */
  const handlePeriodSearch = () => {
    const [start, end] = period;

    if (!start || !end) {
      return MessageAlert.info('검색 기간을 선택해주세요!');
    }
    setIsLoading(true);

    const startDate = dayjs(start).startOf('day').unix();
    const endDate = dayjs(end).endOf('day').unix();

    const filtered = order
      .filter(
        (or) =>
          or.type === selectedTab &&
          or.created_at >= startDate &&
          or.created_at <= endDate,
      )
      .sort((a, b) => b.created_at - a.created_at);

    setTimeout(() => {
      setOrderList(filtered);
      setIsLoading(false);
    }, 500);
  };

  /* ===== Hooks ===== */
  /**
   * 탭별 주문 목록 정제
   * --
   */
  React.useEffect(() => {
    const filtered = order
      .filter((or) => or.type === selectedTab)
      .sort((a, b) => b.created_at - a.created_at);

    setOrderList(filtered);
  }, [selectedTab, order]);

  /* ===== Render ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content
          maxWidth={1820}
          padding={screenSize >= 768 ? '120px 0px 200px' : '60px 0'}
        >
          <Row gutter={25}>
            <Col x={0} md={1} xl={2}></Col>
            <Col x={0} md={6} xl={4}>
              <SideMenu />
            </Col>
            <Col x={24} md={16} xl={16}>
              <Content
                maxWidth={'100%'}
                padding={
                  screenSize >= 1200
                    ? '0 0 0 95px'
                    : screenSize >= 768
                      ? '0 0 0 20px'
                      : '0'
                }
              >
                {/* 주문내역 */}
                <Content maxWidth={'100%'} padding={0}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Title
                      style={{
                        fontSize:
                          screenSize >= 768 ? 32 : screenSize >= 500 ? 28 : 20,
                        fontWeight: 700,
                      }}
                    >
                      주문/배송 조
                    </Title>
                    {/* Taps */}
                    <div style={styles.tabContainer}>
                      {TAP.map((tap) => (
                        <div
                          key={tap.label}
                          style={{
                            ...styles.tab,
                            ...(selectedTab === tap.value
                              ? styles.activeTab
                              : {}),
                          }}
                          onClick={() => {
                            // setSelectedTab(tap.value);
                            navigate(
                              `/mypage/order?type=${tap.value.toLocaleLowerCase()}`,
                            );
                            setCurrentPage(1); // 탭 변경 시 페이지를 초기화
                          }}
                        >
                          {tap.label}
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '32px 0',
                        marginBottom: 80,
                        borderBottom: '1px solid #ddd',
                        flexDirection: screenSize >= 768 ? 'unset' : 'column',
                        gap: screenSize >= 768 ? 'unset' : '10px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          width: screenSize >= 768 ? '51.44%' : '100%',
                          height: 48,
                        }}
                      >
                        <div style={styles.buttonGroup}>
                          {PERIOD.map((period) => (
                            <button
                              key={`${period.value}-${period.key}-${period.label}`}
                              style={{
                                ...styles.button,
                                ...(selected === `${period.value}-${period.key}`
                                  ? styles.activeButton
                                  : {}),
                              }}
                              onClick={() => {
                                handleDateChange(period.value, period.key);
                                setCurrentPage(1); // 기간 변경 시 페이지를 초기화
                              }}
                            >
                              {period.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: screenSize >= 768 ? '46.3%' : '100%',
                        }}
                      >
                        <DatePicker.RangePicker
                          style={{
                            width: '64%',
                            height: 48,
                            border: '1px solid var(--light-gray-color)',
                          }}
                          value={[
                            period?.[0] ? dayjs(period?.[0], DATE_FORMAT) : '',
                            period?.[1] ? dayjs(period?.[1], DATE_FORMAT) : '',
                          ]}
                          format={DATE_FORMAT}
                          placeholder={['시작일', '종료일']}
                          minDate={dayjs().subtract(365, 'day')}
                          maxDate={dayjs()}
                          onChange={(_, dateString) => setPeriod(dateString)}
                        />
                        <Button
                          style={{
                            width: '31.12%',
                            borderRadius: '0',
                            fontWeight: 700,
                            fontSize: screenSize >= 768 ? 16 : 14,
                            backgroundColor: 'var(--white-color)',
                            color: 'var(--black-color)',
                            border: '1px solid var(--black-color)',
                          }}
                          onClick={() => handlePeriodSearch()}
                        >
                          조회
                        </Button>
                      </div>
                    </div>
                  </div>
                  {/* OrderListTable */}
                  <div>
                    {orderList.length ? (
                      orderList
                        .slice((currentPage - 1) * COUNT, currentPage * COUNT)
                        .map((group) =>
                          selectedTab !== 'RENTAL' ? (
                            <React.Fragment
                              key={`shop-order-${group.shop_order_id}`}
                            >
                              <OrderListTable
                                orders={[group]}
                                detailText="주문상세"
                                onDetailClick={(state) =>
                                  navigate(
                                    `/mypage/order/${encodeURIComponent(
                                      encrypt(state.shop_order_id),
                                    )}`,
                                  )
                                }
                                onSubmit={onOrderStatus}
                                showExpectedCycle={
                                  selectedTab === 'SUBSCRIPTION' ? true : false
                                }
                              />
                              <VirtualAccountInfo
                                order={group}
                                screenSize={screenSize}
                              />
                            </React.Fragment>
                          ) : (
                            <RentalListTable
                              key={`shop-order-${group.shop_order_id}`}
                              orders={[group]}
                              detailText="주문상세"
                              onDetailClick={(state) =>
                                navigate(
                                  `/mypage/order/${encodeURIComponent(
                                    encrypt(state.shop_order_id),
                                  )}`,
                                )
                              }
                              onSubmit={onOrderStatus}
                            />
                          ),
                        )
                    ) : (
                      <div style={styles.emptyState}>
                        <Empty description={'주문 내역이 없습니다.'} />
                      </div>
                    )}
                  </div>
                </Content>
                {/* 주문내역 끝 */}
              </Content>

              {/* SECTION: 페이지네이션 */}
              <Content maxWidth={'100%'} padding={0} style={{ marginTop: 100 }}>
                <Pagination
                  total={orderList.length}
                  pageSize={COUNT}
                  current={currentPage}
                  onChange={handlePageChange}
                  showPage={5}
                  buttonStyle={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#999999',
                    background: 'none',
                    width: 28,
                    height: 28,
                  }}
                  isButton={true}
                  activeStyle={{
                    color: `var(--black-color) !important`,
                    borderBottom: '2px solid #111111',
                  }}
                  hoverStyle={{ color: `var(--black-color) !important` }}
                  navStyle={{ border: '1px solid var(--black-color)' }}
                  isLatestButton={true}
                />
              </Content>
            </Col>
            <Col x={0} md={1} xl={2}></Col>
          </Row>
        </Content>
      </Spin>
    </React.Fragment>
  );
};

export default MypageOrderPresenter;
